import autoAnimate from '@formkit/auto-animate';
import { AlertsElement } from 'inclusive-elements';
import { h } from 'vue';

import { createApp } from './app.ts';
import { refreshStickyAfterOffsetChange } from './behaviours/stickyObservers.ts';
import Alert from './components/Alert.vue';
import { payload } from './payload.ts';

const ALERTS_HEIGHT_VAR = '--alerts-height';

export const NETWORK_OFFLINE_ALERT_KEY = 'network-offline';
export const alerts = document.createElement('ui-alerts') as AlertsElement;
alerts.className = 'alerts';
autoAnimate(alerts);
document.body.appendChild(alerts);

let updateOffsetFrame = 0;

function updateAlertsOffset() {
    updateOffsetFrame = 0;

    if (alerts.children.length === 0) {
        document.documentElement.style.removeProperty(ALERTS_HEIGHT_VAR);
        refreshStickyAfterOffsetChange();
        return;
    }

    document.documentElement.style.setProperty(
        ALERTS_HEIGHT_VAR,
        `${Math.ceil(alerts.getBoundingClientRect().height)}px`,
    );
    refreshStickyAfterOffsetChange();
}

function scheduleAlertsOffsetUpdate() {
    if (updateOffsetFrame) return;

    updateOffsetFrame = requestAnimationFrame(updateAlertsOffset);
}

const resizeObserver = new ResizeObserver(scheduleAlertsOffsetUpdate);
resizeObserver.observe(alerts);

new MutationObserver(scheduleAlertsOffsetUpdate).observe(alerts, { childList: true });
window.addEventListener('resize', scheduleAlertsOffsetUpdate, { passive: true });

setTimeout(() => {
    payload.alerts?.forEach((props) => {
        alerts.show(createAlert(props));
    });
}, 100);

export type AlertProps = {
    type?: string;
    icon?: any;
    message?: any;
    action?: {
        label: string;
        click: () => void;
    };
    dismissible?: boolean;
};

export function createAlert(props: AlertProps) {
    const alert = createApp({
        render: () =>
            h(Alert, {
                ...props,
                onDismiss: () => alerts.dismiss(el),
            }),
    });
    const temp = document.createElement('div');
    const el = alert.mount(temp).$el as HTMLElement;
    return el;
}

if (import.meta.env.DEV) {
    let debugAlertIndex = 0;

    window.addEventListener('keydown', (event) => {
        if (!event.shiftKey || !event.ctrlKey || event.key.toLowerCase() !== 'a') {
            return;
        }

        event.preventDefault();

        debugAlertIndex += 1;

        alerts.show(
            createAlert({
                type: 'success',
                dismissible: true,
                message: `Debug alert ${debugAlertIndex}`,
            }),
            { duration: 0 },
        );
    });
}
